import { ISideMenuItemModel } from '../../shared/models/side-menu-item.model';

/**
 * Attribute tables are keyed by column_name ('sub_department_name'); the product filters in
 * side_menu_config are keyed by the short form ('sub_department'). The two have never shared a key,
 * so lining the headings up needs this explicit bridge rather than a lookup on a common field.
 *
 * Only the four hierarchy attributes are mapped. Every other attribute keeps its own display_name.
 */
export const attributeColumnToFilterName: ReadonlyMap<string, string> = new Map<string, string>([
    ['department_name', 'department'],
    ['sub_department_name', 'sub_department'],
    ['category_name', 'category'],
    ['product_type_name', 'product_type']
]);

/**
 * Maps attribute column_name -> the display name the side menu shows for the same field.
 *
 * Returns an empty map whenever the config is missing or does not carry filters, so callers fall
 * straight back to the attribute's own display_name rather than rendering a blank heading.
 */
export function buildAttributeHeaderOverrides(sideMenuConfig: Array<ISideMenuItemModel>): Map<string, string> {
    const overrides: Map<string, string> = new Map<string, string>();

    if (!sideMenuConfig?.length) {
        return overrides;
    }

    const filters: ISideMenuItemModel = sideMenuConfig.find((item: ISideMenuItemModel) => item.name === 'filters');

    // filters -> accordion -> filter groups -> fields. Same traversal the breadcrumbs already use;
    // children[0] is the accordion that holds every group.
    const groups: Array<ISideMenuItemModel> = filters?.children?.[0]?.children ?? [];

    for (const group of groups) {
        for (const field of group?.children ?? []) {
            // Scan every group rather than assuming the 'product' one: the four names are distinct,
            // and a renamed or re-parented group should not silently drop the substitution.
            for (const [columnName, filterName] of attributeColumnToFilterName) {
                if (field?.name === filterName && field.displayName) {
                    overrides.set(columnName, field.displayName);
                }
            }
        }
    }

    return overrides;
}
